import { Check, MessageCircle, Star, Users } from "lucide-react";

export default function PricingSection({ pricing, general }) {
  const moneda = pricing.moneda || "€";
  const whatsappMsg = encodeURIComponent("Hola, me gustaría inscribir a mi hijo/a en el Campus Verano Activo 2026. ¿Quedan plazas?");
  const phone = (general.whatsapp || "657871824").replace(/\D/g, "");
  const whatsappUrl = `https://wa.me/34${phone}?text=${whatsappMsg}`;
  const sectionLabel = pricing.section_label || "Precios y Tarifas";
  const sectionTitle = pricing.title || "Invierte en su aventura";
  const sectionSubtitle = pricing.subtitle || "Precios por semana. ¡Descuento especial para hermanos/as!";

  const plans = [
    {
      title: "Precio Estándar",
      price: pricing.precio_semana || 140,
      badge: null,
      color: "#0072A4",
      features: [
        "Todas las actividades incluidas",
        "Monitores titulados",
        "Materiales y equipos",
        "Seguro de actividades",
        "Horario 9:00 - 14:00",
      ],
    },
    {
      title: "Precio Hermanos/as",
      price: pricing.precio_hermanos || 130,
      badge: "DESCUENTO FAMILIAR",
      color: "#F58220",
      features: [
        "Todo lo incluido en Estándar",
        "Descuento por hermanos/as",
        `Ahorro de ${(pricing.precio_semana || 140) - (pricing.precio_hermanos || 130)}${moneda}/semana`,
        "Mismo grupo si se solicita",
        "Inscripción simultánea",
      ],
    },
  ];

  const extras = [
    { label: "Servicio Madrugadores", desc: "Desde las 8:00h", price: pricing.precio_madrugadores || 20 },
    { label: "Recogida Ampliada", desc: "Hasta las 15:00h", price: pricing.precio_recogida || 20 },
  ];

  return (
    <section id="precios" className="py-14 md:py-20 bg-[#F0F8FF]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#3CB44B]/10 rounded-full px-5 py-2 mb-5">
            <Star size={16} color="#3CB44B" />
            <span className="text-[#3CB44B] font-['Montserrat'] font-bold text-[10px] uppercase tracking-[0.18em]">{sectionLabel}</span>
          </div>
          <h2
            className="font-['Montserrat'] font-extrabold text-[#0F172A] mb-5"
            style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.625rem)", letterSpacing: "-0.01em", lineHeight: 1.2 }}
            data-testid="pricing-title"
          >
            {sectionTitle}
          </h2>
          <p className="text-[#475569] font-['Montserrat'] font-medium text-sm md:text-base max-w-xl mx-auto">
            {sectionSubtitle}
          </p>
        </div>

        {/* Main pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto mb-12">
          {plans.map((plan) => (
            <div
              key={plan.title}
              data-testid={`pricing-card-${plan.title.replace(/\s+/g, '-').toLowerCase()}`}
              className={`bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all border-2 ${
                plan.badge ? "border-[#F58220]" : "border-[#0072A4]/20"
              }`}
            >
              {/* Header card */}
              <div className="flex items-start justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${plan.color}20` }}>
                    <Users size={20} color={plan.color} />
                  </div>
                  <h3 className="font-['Montserrat'] font-bold text-sm text-[#0F172A]">{plan.title}</h3>
                </div>
                {plan.badge && (
                  <span className="whitespace-nowrap font-['Montserrat'] font-bold text-[10px] px-3 py-1.5 rounded-full uppercase tracking-wider flex-shrink-0"
                    style={{ background: `${plan.color}18`, color: plan.color }}>
                    {plan.badge}
                  </span>
                )}
              </div>

              <div className="flex items-end gap-2 mb-6">
                <span className="font-['Montserrat'] font-black text-4xl" style={{ color: plan.color }}>
                  {plan.price}{moneda}
                </span>
                <span className="text-[#475569] font-['Montserrat'] text-sm mb-1.5">/ semana</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${plan.color}20` }}>
                      <Check size={12} color={plan.color} />
                    </div>
                    <span className="font-['Montserrat'] text-[#475569] text-sm">{feat}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#inscripcion"
                data-testid={`pricing-cta-${plan.title.replace(/\s+/g, '-').toLowerCase()}`}
                className="block w-full text-center py-2.5 rounded-full font-['Montserrat'] font-bold text-sm transition-all active:scale-95 hover:opacity-90"
                style={{ background: plan.color, color: "white" }}
              >
                Inscribirse
              </a>
            </div>
          ))}
        </div>

        {/* Optional services */}
        <div className="max-w-3xl mx-auto">
          <h3 className="font-['Montserrat'] font-bold text-base text-[#0F172A] mb-4 text-center">Servicios Opcionales</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {extras.map((extra) => (
              <div
                key={extra.label}
                data-testid={`extra-service-${extra.label.replace(/\s+/g, '-').toLowerCase()}`}
                className="bg-white rounded-2xl p-5 border border-[#E2E8F0] flex items-center justify-between hover:border-[#F5C518] transition-colors"
              >
                <div>
                  <div className="font-['Montserrat'] font-bold text-[#0F172A] text-sm">{extra.label}</div>
                  <div className="font-['Montserrat'] text-[#475569] text-xs">{extra.desc}</div>
                </div>
                <div className="font-['Montserrat'] font-bold text-lg text-[#F58220]">+{extra.price}{moneda}</div>
              </div>
            ))}
          </div>

          {/* Info boxes */}
          <div className="space-y-3">
            {pricing.metodo_pago && (
              <div className="bg-white rounded-xl p-4 border border-[#E2E8F0] text-sm text-[#475569] font-['Montserrat']">
                <strong className="text-[#0F172A]">Pago:</strong> {pricing.metodo_pago}
              </div>
            )}
            {pricing.reserva_info && (
              <div className="bg-[#3CB44B]/5 rounded-xl p-4 border border-[#3CB44B]/20 text-sm text-[#475569] font-['Montserrat']">
                <strong className="text-[#27AE60]">Reserva:</strong> {pricing.reserva_info}
              </div>
            )}
          </div>

          {/* Confirm WhatsApp CTA */}
          <div className="mt-8 text-center">
            <p className="font-['Montserrat'] text-[#475569] text-sm mb-4">
              Antes de pagar, confirma disponibilidad de plazas:
            </p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#3CB44B] text-white font-['Montserrat'] font-bold px-8 py-3 rounded-full hover:bg-[#2E9B3A] transition-all active:scale-95 shadow-lg"
            >
              <MessageCircle size={18} />
              Confirmar plaza por WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
